import { CurrencyPipe } from '../../common/pipes/currency-format.pipe';
import { AppMessageService } from '../../app-message.service';
import { Cart } from '../models/cart.model';
import { Item } from '../models/item.model';
import { CommonService } from '../../shared/services/common.service';
import { ApiUrlConstants } from '../../common/constants/api.constants';
import { CartTypes } from '../enums/cart-types.enum';
import { Injectable } from '@angular/core';
import { CacheService } from '../../shared/services/cache.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import { Product } from '../../products/interfaces/product.interface';
import remove from 'lodash-es/remove';
import { CacheKey } from '../../shared/constants/cachekey.constants';
import { ChildImageFilterPipe } from '../../common/pipes/child-image-filter.pipe';
import { ImageType } from '../../products/enums/image-type.enum';
import { Member } from '../interfaces/member.interface';
import { ConfigurationService } from './configuration.service';
import { StoreConfig } from '../interfaces/store-config.interface';
import { CartSummary } from '../interfaces/cart-summary.interface';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/internal/operators/catchError';
import { throwError } from 'rxjs/internal/observable/throwError';

@Injectable()
export class CartService {
  constructor(
    private _http: HttpClient,
    private _cacheService: CacheService,
    private _commonService: CommonService,
    private _childImageFilterPipe: ChildImageFilterPipe,
    private _appMessageService: AppMessageService,
    private _configurationService: ConfigurationService,
    private _currencyPipe: CurrencyPipe
  ) { }

  /**
   * @param  {string} cartSessionId
   * @returns Observable
   */
  getCartSessionInfo(cartSessionId: string, cartTypeId: number): Observable<Cart> {
    const storeConfig: StoreConfig = this._cacheService.get(CacheKey.StoreConfiguration);
    return (this._http
      .get<Cart>(decodeURIComponent(encodeURIComponent(ApiUrlConstants.orderApiUrl + '/V1/stores/' + 
      storeConfig.id + '/cart-types/' + cartTypeId +
        '/carts/' + cartSessionId)))
        .pipe(catchError(this.handleErrorObservable)));
  }

  /**
   * @description To get Member cart items based on the cart type
   * @date 2018-09-03
   * @param {number} memberId
   * @param {number} cartTypeId
   * @returns {Observable<Cart[]>}
   * @memberof CartService
   */
  getCartItemsByMemberIdAndCartType(memberId: number, cartTypeId: number): Observable<Cart> {
    const storeConfig: StoreConfig = this._cacheService.get(CacheKey.StoreConfiguration);
    return (this._http
      .get<Cart>(ApiUrlConstants.orderApiUrl + '/V1/stores/' + storeConfig.id + '/members/' + memberId + '/cart-types/' + cartTypeId + '/carts')
      .pipe(catchError(this.handleErrorObservable)));
  }

  /**
   * @param  {Product} product
   * @param  {CartTypes} cartType
   * @param  {any} cart?
   * @returns Observable
   */
  saveCart(cartItems: Item[], cartType: CartTypes, cart?: Cart): Observable<any> {
    const storeConfig: StoreConfig = this._cacheService.get(CacheKey.StoreConfiguration);
    const data = this.prepareCartItem(storeConfig.id, cartItems, cartType, cart);
    return this._http
      .post(ApiUrlConstants.orderApiUrl + '/V1/stores/' + storeConfig.id + '/cartItems/', JSON.stringify(data))
      .pipe(catchError(this.handleErrorObservable));;
  }

  /**
   * @param  {Product} product
   * @param  {CartTypes} cartType
   * @param  {any} cartItem
   * @param  {Cart} cartSession
   * @returns Observable
   */
  updateCart(items: Item[], cartType: CartTypes, cartItem: any, cartSession: Cart): Observable<any> {
    const storeConfig: StoreConfig = this._cacheService.get(CacheKey.StoreConfiguration);
    let cart: any = {};
    cart = this.prepareUpdateCartItem(storeConfig.id, cartSession, items, cartType);
    return this._http
      .put<any>(ApiUrlConstants.orderApiUrl + '/V1/stores/' + storeConfig.id + '/cartItems/' + cartSession.sessionGuid, JSON.stringify(cart))
      .pipe(catchError(this.handleErrorObservable));
  }


  /**
   * @param  {string} cartSessionId
   * @param  {number} cartItemId
   * @returns Observable
   */
  removeCartItem(cartSessionId: string, cartItemId: number): Observable<boolean> {
    if (cartItemId) {
    const storeConfig: StoreConfig = this._cacheService.get(CacheKey.StoreConfiguration);
    return this._http
    .delete<boolean>(ApiUrlConstants.orderApiUrl + '/V1/stores/' + storeConfig.id + '/cart/' + cartSessionId + '/cartItems/' + cartItemId)
    .pipe(catchError(this.handleErrorObservable));
    } else {
      return Observable.throw('cartItem id is empty');
    }

  }

  /**
   * @param  {string} cartSessionId
   * @returns Observable
   */
  removeCart(cartSessionId: string): Observable<boolean> {
    const storeConfig: StoreConfig = this._cacheService.get(CacheKey.StoreConfiguration);
    return this._http
    .delete<boolean>(ApiUrlConstants.orderApiUrl + '/V1/stores/' + storeConfig.id + '/cart/' + cartSessionId)
    .pipe(catchError(this.handleErrorObservable));
  }

  /**
   * reloading cart info when user login into system
   * @param  {Product} products
   * @param  {Cart} cartSession
   * @returns Cart
   */
  reloadCartSession(products: Product[], cartSession: Cart): Cart {
    if (products && products.length > 0 && cartSession) {
      const items: Item[] = [];
      products.forEach(x => {
        items.push(this.convertProductToItemMappper(x, cartSession));
      });
      cartSession.items = items;
    }
    return cartSession;
  }

  /**
   * merge cart items to saved cart in local storage
   * @returns void
   */
  mergeCartSession(item: Item, cartResponse: any, cartSessionInfo: Cart): Cart {
    if (item && cartResponse && cartResponse.items && cartResponse.items.length > 0) {
      const savedItem: any = cartResponse.items.find((x: any) => x.productId === item.productId);
      if (savedItem) {
        item.shoppingCartId = savedItem.shoppingCartId;
        item.id = savedItem.id;
      }
      if (cartSessionInfo && cartSessionInfo.items && cartSessionInfo.items.length > 0) {
        if (cartSessionInfo.id !== cartResponse.id) {
          cartSessionInfo.id = cartResponse.id;
          for (let index = 0; index < cartSessionInfo.items.length; index++) {
            const responseProduct = cartResponse.items.find(product => cartSessionInfo.items[index].productId === product.productId);
            if (responseProduct) {
              cartSessionInfo.items[index].id = responseProduct.id;
              cartSessionInfo.items[index].shoppingCartId = cartResponse.id;
            }
          }
        }
      }
    }
    if (cartSessionInfo && cartSessionInfo.items && cartSessionInfo.items.length > 0) {
      const cartItem: any = cartSessionInfo.items.find(x => x.productId === item.productId);
      const cartItemIndex: number = cartSessionInfo.items.findIndex(x => x.productId === item.productId);
      if (cartItem) {
        cartSessionInfo.items = remove(cartSessionInfo.items, function (n) {
          return n.productId !== item.productId;
        });
      }
      cartSessionInfo.items.splice(cartItemIndex, 0, item);
      return cartSessionInfo;
      // this._cacheService.set(CacheKey.CartSessionInfo, cartSessionInfo);
    } else if (cartResponse) {
      cartResponse.items = [];
      cartResponse.items.push(item);
      const store: StoreConfig = this._configurationService.getStoreData();
      const cart: Cart = new Cart(cartResponse, store);
      return cart;
    }
  }

  /**
   * convert product to cart item  mapper
   * @returns Item
   */
  convertProductToItemMappper(product: Product, cartResponse?: Cart): Item {
    const item: Item = new Item();
    if (cartResponse && cartResponse.items && cartResponse.items.length > 0) {
      const cartItem: Item = cartResponse.items.find(x => x.productId === product.id);
      if (cartItem) {
        item.shoppingCartId = cartItem.shoppingCartId;
        item.id = cartItem.id;
      }
    }
    item.applicablePrice = this._currencyPipe.getFormatedCurrency(product.applicablePrice);
    item.catalogPrice = this._currencyPipe.getFormatedCurrency(
      product.catalogPrice
    );
    item.baseRetailPrice = this._currencyPipe.getFormatedCurrency(
      product.baseRetailPrice
    );
    item.baseWholesalePrice = this._currencyPipe.getFormatedCurrency(
      product.baseWholesalePrice
    );
    item.CV = !product.shipping.literature ? product.commissionableVolume : 0;
    item.itemNumber = product.itemNumber;
    item.productId = product.id;
    item.name = product.name;
    item.displayName = product.displayName;
    item.quantity = product.productQuantity;
    item.retailPrice = this._currencyPipe.getFormatedCurrency(product.retailPrice);
    item.shortDescription = product.shortDescription;
    item.slug = product.slug;
    item.height = product.shipping.height;
    item.weight = product.shipping.weight;
    item.width = product.shipping.width;
    item.measurementUnitLengthValue = product.shipping.measurementUnitLengthValue;
    item.measurementUnitMassValue = product.shipping.measurementUnitMassValue;
    item.isLiterature = product.shipping.literature;
    item.isVirtualItem = product.virtualProduct;
    item.shippingRequired = product.shipping.shippingRequired;
    item.wholesalePrice = this._currencyPipe.getFormatedCurrency(product.wholesalePrice);
    item.categoryImageUrl = this._childImageFilterPipe.transform(product.imageModelList, ImageType.Category);
    item.iconUrl = this._childImageFilterPipe.transform(product.imageModelList, ImageType.MiniCart);
    item.shoppingCartUrl = this._childImageFilterPipe.transform(product.imageModelList, ImageType.ShoppingCart);
    item.totalPoints = item.calculateTotalPoints();
    item.totalPrice = this._currencyPipe.getFormatedCurrency(item.calculateTotalPrice(item.applicablePrice, item.quantity));
    item.stockAvailable = product.stockAvailable;
    item.backOrderAllowed = product.backOrderAllowed;
    item.manageStock = product.manageStock;
    item.stockQuantity = product.stockQuantity;
    item.computedTaxInclusivePrice = product.computedTaxInclusivePrice;
    return item;
  }
  /**
   * @param  {Product} product
   * @param  {CartTypes} cartType
   * @param  {any} cart?
   * @returns any
   */
  private prepareCartItem(storeId: number, cartItems: Item[], cartType: CartTypes, cart?: any): any {
    const userInfo: Member = this._cacheService.get(CacheKey.UserInfo);
    if (!cart) {
      cart = {};
    }
    if (userInfo) {
      cart.memberId = userInfo.memberId;
    }
    cart.items = [];
    if (cartItems && cartItems.length > 0) {
      cartItems.forEach(itm => {
        const cartItem = new Item();
        if (itm) {
          cartItem.quantity = itm.quantity;
          cartItem.productId = itm.productId;
          cart.items.push(cartItem);
        }
      });
    }
    cart.cartTypeId = cartType;
    cart.storeId = storeId;
    cart.purchaseFlowId = 1;
    return cart;
  }

  /**
   * @description To get member Cart Summary
   * @date 2018-09-03
   * @param {number} memberId
   * @param {number} cartTypeId
   * @returns {Observable<Cart[]>}
   * @memberof CartService
   */
  getMemberCartSummary(memberId: number): Observable<CartSummary[]> {
    const storeConfig: StoreConfig = this._cacheService.get(CacheKey.StoreConfiguration);
    // https://apigw-titandev1.nextsphere.com/api/order/V1/stores/1/members/1/cart-summary
    return (this._http
      .get<CartSummary[]>(ApiUrlConstants.orderApiUrl + '/V1/stores/' + storeConfig.id + '/members/' + memberId + '/cart-summary')
      .pipe(catchError(this.handleErrorObservable)));
  }


  /**
  * @param  {any} cartItem
  * @param  {any} cart
  * @param  {Product} product
  * @param  {CartTypes} cartType
  * @returns any
  */
  private prepareUpdateCartItem(storeId: number, cart: any, items: Item[], cartType: CartTypes): any {
    const userInfo: Member = this._cacheService.get(CacheKey.UserInfo);
    cart.items = cart.items || [];
    cart.cartTypeId = cartType;
    cart.purchaseFlowId = 1;
    cart.storeId = storeId;
    if (items && items.length > 0) {
      items.forEach(itm => {
        let cartItem = new Item();
        if (cart && cart.items && cart.items.length > 0) {
          cartItem = cart.items.find(x => x.productId === itm.productId) || new Item();
        }
        if (itm) {
          cartItem.quantity = itm.quantity;
          cartItem.productId = itm.productId;
          if (cartItem && cartItem.shoppingCartId) {
            const cartItemIndex: number = cart.items.findIndex(x => x.productId === itm.productId);
            cart.items = remove(cart.items, function (n: any) {
              return n.productId !== cartItem.productId;
            });
            cart.items.splice(cartItemIndex, 0, cartItem);
          } else {
            cart.items.push(cartItem);
          }
        }
      });
    }
    if (userInfo) {
      cart.memberId = userInfo.memberId;
    }
    return cart;
  }

   /**
   *
   * @param cart
   * @param store
   */
  reloadCart(cart: Cart): Cart {
    const store = this._configurationService.getStoreData();
    const userInfo: Member = this._cacheService.get(CacheKey.UserInfo);
    return new Cart(cart, store, 0, 0, store.taxInclusive,
      this._commonService.getMemberVatAndDiscountInfo(userInfo, false));
  }



  /**
   * To handle the obervable error response
   * @param  {Response|any} error
   */
  private handleErrorObservable(err) {
    // in a real world app, we may send the server to some remote logging infrastructure
    // instead of just logging it to the console
    let errorMessage: string;
    if (err.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMessage = `Backend returned code ${err.status}: ${err.error}`;
    }
    console.error(err);
    return throwError(errorMessage);
  }
}
